/**
 * KPI strip — Drilldown Tab Header Standard §7.
 *
 * Bare tiles on the page background. NEVER wrapped in a container card: a
 * wrapper re-creates the nesting problem the whole two-tier pattern exists to
 * avoid.
 *
 * Rules this component enforces so callers can't get them wrong:
 *   • Empty values render as an em dash in `--muted` — never `0`, `N/A` or
 *     blank. `$0` and "no data" are different facts and must look different.
 *   • Status tiles use the soft-fill + line-border pair, and never rely on fill
 *     alone: a toned tile always keeps its label (and takes an optional icon).
 *   • Grid is `repeat(auto-fit, minmax(150px, 1fr))` at a 9px gap, dropping to
 *     two-up below 560px.
 *
 * Target 4–6 tiles. Below 4, use inline meta text in the header instead; above
 * 6, the figures stop being scannable and something belongs in a card. The
 * component logs neither — that's a review conversation, not a runtime error.
 */
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { memo } from "react";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { EM_DASH, TAB_TOKENS as T, TONE_STYLES } from "./tabTokens";

const isEmptyValue = (v) => v === null || v === undefined || v === "";

export const KpiTile = memo(
  ({ label, value, subtitle, icon, tone = "neutral", progress }) => {
    const style = TONE_STYLES[tone] || TONE_STYLES.neutral;
    const empty = isEmptyValue(value);
    // Optional completion bar (0..1) — e.g. "3 of 6 package checks". Uses the
    // tile's tone ink so a warn/ok tile's bar matches its value.
    const hasBar = typeof progress === "number" && !Number.isNaN(progress);
    const barInk = tone === "neutral" ? T.color.brand : style.ink;

  return (
    <Flx
      column
      sx={{
        gap: "5px",
        padding: "10px 12px",
        background: style.fill,
        border: `1px solid ${style.line}`,
        borderRadius: T.radius.sm,
        minWidth: 0,
      }}
    >
      <Flx ac sx={{ gap: "5px", minWidth: 0 }}>
        {icon && (
          <Box
            sx={{
              display: "flex",
              flexShrink: 0,
              color: tone === "neutral" ? T.color.muted : style.ink,
              "& svg": { fontSize: 12 },
            }}
          >
            {icon}
          </Box>
        )}
        <Txt
          sx={{
            ...T.type.kpiLabel,
            ...(tone !== "neutral" && { color: style.ink }),
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Txt>
      </Flx>

      <Txt
        sx={{
          ...T.type.kpiValue,
          ...(empty && { color: T.color.muted }),
          ...(!empty && tone !== "neutral" && { color: style.ink }),
        }}
      >
        {empty ? EM_DASH : value}
      </Txt>

      {hasBar && (
        <Box
          sx={{
            height: 4,
            borderRadius: "99px",
            background: "rgba(15,23,42,.08)",
            overflow: "hidden",
            marginTop: "2px",
          }}
        >
          <Box
            sx={{
              height: "100%",
              borderRadius: "99px",
              background: barInk,
              width: `${Math.max(0, Math.min(1, progress)) * 100}%`,
              transition: "width 200ms ease",
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          />
        </Box>
      )}

      {subtitle && (
        <Txt sx={{ ...T.type.meta, fontSize: "10.5px" }}>{subtitle}</Txt>
      )}
    </Flx>
    );
  },
);

const LoanTabKpiStrip = ({ items = [], minWidth = 150, sx }) => {
  if (!items.length) return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`,
        gap: T.space.kpiGap,
        [T.bp.twoUp]: { gridTemplateColumns: "repeat(2, 1fr)" },
        ...sx,
      }}
    >
      {items.map((item, i) => (
        <KpiTile key={item.key || item.label || i} {...item} />
      ))}
    </Box>
  );
};

KpiTile.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  tone: PropTypes.oneOf(["neutral", "danger", "warn", "ok", "brand"]),
  /** Optional completion ratio 0..1 — renders a thin bar under the value. */
  progress: PropTypes.number,
};

LoanTabKpiStrip.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape(KpiTile.propTypes)),
  minWidth: PropTypes.number,
  sx: PropTypes.object,
};

KpiTile.displayName = "KpiTile";

export default memo(LoanTabKpiStrip);
